import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import notificacionService from '../services/notificacionService';

const MAX_EN_PANEL = 20;

// Correos entrantes (Gmail/Outlook). Extraído de components/MailCampana.js para compartir
// fuente con la campana unificada mobile. Expone window.recargarMailCampana, llamado desde
// otras pantallas para forzar un refresco inmediato.
//
// OJO: el conteo de no leídos se calcula DESPUÉS de cortar a los MAX_EN_PANEL(20) correos más
// recientes — igual que el componente original. Si hay más de 20 correos sin leer, el contador
// no ve a todos. Es el comportamiento actual, no se "corrige" acá.
export default function useNotificacionesCorreo() {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const cargarEmails = useCallback(async () => {
        try {
            const res = await notificacionService.getCorreos();
            const todos = Array.isArray(res?.data) ? res.data : [];
            todos.sort((a, b) => new Date(b.fechaProgramada) - new Date(a.fechaProgramada));
            setEmails(todos.slice(0, MAX_EN_PANEL));
        } catch (err) {
            console.error('Error al cargar emails:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const marcarLeido = useCallback(async (id) => {
        if (!id) return;
        try {
            await notificacionService.marcarComoLeidaUsuario(id);
            setEmails(prev => prev.map(e => e.id === id ? { ...e, leido: true } : e));
        } catch (err) {
            console.error('Error al marcar email como leído:', err);
        }
    }, []);

    // { onNavegar } se llama justo antes de navegar — cada consumidor lo usa para cerrar su
    // propio overlay (popover desktop, sheet mobile), mismo orden que tenía el componente original.
    const abrirEmail = useCallback(async (item, { onNavegar } = {}) => {
        if (!item.leido) await marcarLeido(item.id);
        onNavegar?.();
        navigate(`/correos?mailId=${item.id}`);
    }, [navigate, marcarLeido]);

    const descartar = useCallback(async (item) => {
        if (!item.leido) await marcarLeido(item.id);
        setEmails(prev => prev.filter(email => email.id !== item.id));
    }, [marcarLeido]);

    useEffect(() => {
        cargarEmails();
        const intervalo = setInterval(cargarEmails, 60000);
        window.recargarMailCampana = cargarEmails;
        return () => {
            clearInterval(intervalo);
            delete window.recargarMailCampana;
        };
    }, [cargarEmails]);

    const noLeidosCount = emails.filter(e => e?.leido === false).length;

    return {
        emails,
        loading,
        noLeidosCount,
        hayNoLeidos: noLeidosCount > 0,
        abrirEmail,
        descartar,
        recargar: cargarEmails,
    };
}
